import React, {useMemo, useState} from 'react';
import type {List} from 'immutable';

import {Anchor, Button, Icon, SearchInput, Text} from '@albert-io/atomic';
import {AddCoteacherModal} from 'client/Classrooms/Classroom/Teacher/People/Coteachers/AddCoteacher/AddCoteacher.react';
import {coteachersHelpArticleUrl} from 'client/Classrooms/Classroom/Teacher/People/Coteachers/coteachers.utils';
import RemoveCoteacher from 'client/Classrooms/Classroom/Teacher/People/Coteachers/RemoveCoteacher/RemoveCoteacher.react';
import {EmptyState, EmptyIllustration} from 'client/TeacherDashboard/components/EmptyState';

import {RosterTable} from './RosterTable';
import type {RosterColumn} from './RosterTable';
import {useClientSort} from './useClientSort';
import type {RosterTeacher} from './usePeopleRoster';

const getSortValue = (teacher: RosterTeacher, key: string): string =>
  key === 'email' ? teacher.getEmail() : teacher.getDisplayName();

const AddCoteacherButton = ({
  roundness
}: {
  roundness?: React.ComponentProps<typeof Button>['roundness'];
}) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <Button onClick={() => setShowModal(true)} roundness={roundness}>
        <Icon className='mr-x1' icon='user-plus' iconStyle='regular' />
        Add co-teacher
      </Button>
      {showModal && <AddCoteacherModal handleClose={() => setShowModal(false)} />}
    </>
  );
};

interface Props {
  coteachers: List<RosterTeacher>;
  currentTeacherId: string;
}

export const TeachersTab = ({coteachers, currentTeacherId}: Props) => {
  const [search, setSearch] = useState('');

  // The class owner counts as a teacher; >1 means there's a real co-teacher to manage.
  const hasCoteachers = coteachers.size > 1;

  const filtered = useMemo(() => {
    const all = coteachers.toArray();
    const term = search.trim().toLowerCase();
    if (!term) {
      return all;
    }
    return all.filter((teacher) =>
      `${teacher.getDisplayName()} ${teacher.getEmail()}`.toLowerCase().includes(term)
    );
  }, [coteachers, search]);

  const {sorted, sortKey, sortDir, toggleSort} = useClientSort(filtered, getSortValue, 'name');

  if (!hasCoteachers) {
    return (
      <EmptyState
        action={<AddCoteacherButton roundness='pill' />}
        illustration={<EmptyIllustration icon='person-chalkboard' />}
      >
        <div className='flex flex-col gap-x1'>
          <Text as='div' size='l'>
            No co-teachers
          </Text>
          <Text as='div' className='max-w-[440px]' color='secondary'>
            Co-teachers can invite students, make assignments, and more!{' '}
            <Anchor
              as='a'
              className='content-tertiary'
              href={coteachersHelpArticleUrl}
              inheritColor
              rel='noopener noreferrer'
              target='_blank'
              underlined
            >
              Learn more.
            </Anchor>
          </Text>
        </div>
      </EmptyState>
    );
  }

  const columns: RosterColumn<RosterTeacher>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (teacher) => (
        <>
          {teacher.getDisplayName()}
          {teacher.getId() === currentTeacherId ? ' (You)' : ''}
        </>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half break-all',
      render: (teacher) => teacher.getEmail()
    },
    {
      key: 'actions',
      srOnlyHeader: 'Actions',
      cellClassName: 'whitespace-nowrap px-x2 py-x1-half text-right',
      render: (teacher) => <RemoveCoteacher teacher={teacher} />
    }
  ];

  return (
    <section className='flex flex-col gap-x3 pt-x4'>
      <div className='flex flex-wrap items-center justify-between gap-x2'>
        <SearchInput
          className='w-[256px]'
          placeholder='Search by name or email'
          rectangle
          value={search}
          onChange={(_e, value) => setSearch(value)}
          onReset={() => setSearch('')}
        />
        <AddCoteacherButton />
      </div>
      <RosterTable
        ariaLabel='Co-teachers'
        columns={columns}
        getRowKey={(teacher) => teacher.getId()}
        noResultsMessage='No co-teachers match your search.'
        onSort={toggleSort}
        rows={sorted}
        sortDir={sortDir}
        sortKey={sortKey}
      />
    </section>
  );
};
